import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import LiquorOutlinedIcon from "@mui/icons-material/LiquorOutlined";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import CustomDataGrid from "../DataGrid/DataGrid";
import InventoryEmptyState from "./InventoryEmptyState";
import LooseStock from "./LooseStock";
import LocationSelect from "./pickers/LocationSelect";
import SkuSelect from "./pickers/SkuSelect";
import { baseUnitNameOf, factorOf, unitOptionTokens, useUnitName } from "./pickers/useUnitName";
import axios from "../../utils/axios";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchOpenContainers,
  fetchExpiringContainers,
  openContainer,
  finishContainer,
} from "../../store/openContainerStore/openContainerThunk";
import { ApiStatus, defaultRowsPerPageOptions } from "../../utils/api.types";

const OpenContainersList: FC = () => {
  const dispatch = useAppDispatch();
  const { containers, status } = useAppSelector((state) => state.openContainers);
  const { locations } = useAppSelector((state) => state.locations);

  const [locationId, setLocationId] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);
  // Cards (loose stock per item and batch, as on the POS) or the plain table of open containers.
  const [tableView, setTableView] = useState(false);
  const [looseReload, setLooseReload] = useState(0);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  // Loose stock is per location, so start on the first one the user can see.
  useEffect(() => {
    if (!locationId && locations?.length) {
      const first = locations.find((l: any) => l.isActive !== false) || locations[0];
      setLocationId(first._id);
    }
  }, [locations, locationId]);

  const locationName = (locations || []).find((l: any) => l._id === locationId)?.name || "";

  // open-a-bottle dialog state
  const [dSku, setDSku] = useState<any>(null);
  const [dSkuId, setDSkuId] = useState("");
  const [dLocationId, setDLocationId] = useState("");
  const [dContainerUnit, setDContainerUnit] = useState("");
  // Batches with stock at the picked location; only a batch-tracked item has any.
  const [dBatches, setDBatches] = useState<any[]>([]);
  const [dBatchId, setDBatchId] = useState("");
  const [dBatchesLoading, setDBatchesLoading] = useState(false);
  const [dOpening, setDOpening] = useState(false);
  const unitName = useUnitName();

  const isBatchTracked = !!dSku?.isBatchTracked;
  const baseUnit = baseUnitNameOf(dSku, unitName);
  const unitFactor = factorOf(dSku, dContainerUnit);

  // When an item is picked, default the container unit to the pack it already opens,
  // else its first pack (e.g. "bottle"), else the base unit.
  const onChangeSku = (sku: any) => {
    setDSku(sku);
    setDSkuId(sku?._id || "");
    const tokens = unitOptionTokens(sku);
    const packs = sku?.unitConversions ? Object.keys(sku.unitConversions) : [];
    const current = sku?.looseContainerUnit?._id ? String(sku.looseContainerUnit._id) : sku?.looseContainerUnit ? String(sku.looseContainerUnit) : "";
    setDContainerUnit(tokens.includes(current) ? current : packs[0] || tokens[0] || "");
  };

  // Reload the batch list whenever the item or the location changes. Nearest
  // expiry comes first, so the first batch is the one to open next.
  useEffect(() => {
    if (!dialogOpen || !dSkuId || !dLocationId || !isBatchTracked) {
      setDBatches([]);
      setDBatchId("");
      setDBatchesLoading(false);
      return;
    }
    let active = true;
    setDBatchesLoading(true);
    Promise.all([
      axios.get(`/api/v1/erp/batches?skuId=${dSkuId}&isActive=true`),
      axios.get(`/api/v1/erp/stock/on-hand?skuId=${dSkuId}&locationId=${dLocationId}`),
    ])
      .then(([batchRes, stockRes]) => {
        if (!active) return;
        const qty: Record<string, number> = {};
        (stockRes.data?.byBatch || []).forEach((r: any) => { if (r.batchId) qty[String(r.batchId)] = Number(r.qty) || 0; });
        const rows = (Array.isArray(batchRes.data) ? batchRes.data : [])
          .filter((b: any) => (qty[String(b._id)] || 0) > 0)
          .map((b: any) => ({ ...b, onHand: qty[String(b._id)] }));
        setDBatches(rows);
        setDBatchId(rows[0]?._id || "");
      })
      .catch(() => { if (active) setDBatches([]); })
      .finally(() => { if (active) setDBatchesLoading(false); });
    return () => { active = false; };
  }, [dialogOpen, dSkuId, dLocationId, isBatchTracked]);

  const num = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(2));

  // Same "Exp: d MMM yyyy" as the batch chip on the stock screens.
  const batchLabel = (b: any) => {
    const expiry = b.expiryDate ? `Exp: ${new Date(b.expiryDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : "No expiry";
    return `${b.batchNumber || "-"} \u00b7 ${expiry}`;
  };

  // Picked batch's stock at the location, and what that is in the chosen pack:
  // "Stock: 2248 MILLILITRE = 2 BOTTLES (BTL) + 748 MILLILITRE".
  const batchStockText = () => {
    const b = dBatches.find((x) => x._id === dBatchId);
    if (!b) return undefined;
    const stock = Number(b.onHand) || 0;
    const text = `Stock: ${num(stock)} ${baseUnit}`;
    if (!dContainerUnit || unitFactor <= 0) return text;
    const packs = Math.floor(stock / unitFactor + 1e-9);
    const rest = stock - packs * unitFactor;
    return `${text} = ${packs} ${unitName(dContainerUnit)}${rest > 1e-9 ? ` + ${num(rest)} ${baseUnit}` : ""}`;
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDSkuId("");
    setDSku(null);
    setDContainerUnit("");
    setDBatches([]);
    setDBatchId("");
  };

  const reload = () => {
    const params = locationId ? `locationId=${locationId}` : "";
    if (expiringOnly) dispatch(fetchExpiringContainers(params));
    else dispatch(fetchOpenContainers(`${params}${params ? "&" : ""}status=open`));
  };

  useEffect(() => {
    reload();
  }, [dispatch, locationId, expiringOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const canOpen = !dOpening && !!dSkuId && !!dLocationId && !!dContainerUnit && (!isBatchTracked || !!dBatchId);

  const onOpen = async () => {
    if (!canOpen) return;
    setDOpening(true);
    try {
      await dispatch(openContainer({
        skuId: dSkuId,
        locationId: dLocationId,
        containerUnit: dContainerUnit,
        batchId: isBatchTracked ? dBatchId : undefined,
      })).unwrap();
      closeDialog();
      reload();
      setLooseReload((n) => n + 1);
    } catch {
      /* toast handled in slice */
    } finally {
      setDOpening(false);
    }
  };

  const onFinish = async (id: string) => {
    try {
      await dispatch(finishContainer(id)).unwrap();
      reload();
    } catch {
      /* toast handled in slice */
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "sku", headerName: "Item", flex: 1.3, minWidth: 150, valueGetter: (p: GridValueGetterParams) => p.row.skuId?.name || "" },
      { field: "location", headerName: "Location", flex: 1, minWidth: 120, valueGetter: (p: GridValueGetterParams) => p.row.locationId?.name || "" },
      {
        field: "remaining",
        headerName: "Remaining / Opened",
        flex: 1,
        minWidth: 150,
        valueGetter: (p: GridValueGetterParams) => `${p.row.remainingVolume ?? 0} / ${p.row.openedVolume ?? 0} ${p.row.skuId?.baseUnitName || ""}`,
      },
      {
        field: "expiresAt",
        headerName: "Expires",
        flex: 1,
        minWidth: 130,
        renderCell: ({ row }: GridValueGetterParams) => {
          if (!row.expiresAt) return <span>—</span>;
          const exp = new Date(row.expiresAt);
          const overdue = exp.getTime() <= Date.now();
          return <span style={{ color: overdue ? "#c33b3b" : "inherit" }}>{exp.toLocaleDateString()}</span>;
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        minWidth: 90,
        renderCell: ({ row }: GridValueGetterParams) => <Chip size="small" label={row.status} />,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.7,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) =>
          row.status === "open" ? (
            <Button size="small" onClick={() => onFinish(row._id)}>
              Finish
            </Button>
          ) : null,
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2} sx={{ py: 1, alignItems: "center" }}>
        <Grid item xs={12} sm={8} sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ width: 240 }}>
            <LocationSelect value={locationId} onChange={setLocationId} label="Filter by location" activeOnly={false} />
          </Box>
          {!tableView && (
            <TextField
              size="small"
              placeholder="Search item or batch"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ width: 220 }}
            />
          )}
          <FormControlLabel
            control={<Switch checked={tableView} onChange={(e) => setTableView(e.target.checked)} />}
            label="All open containers"
          />
          {tableView && (
            <FormControlLabel
              control={<Switch checked={expiringOnly} onChange={(e) => setExpiringOnly(e.target.checked)} />}
              label="Expiring only"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={4} sx={{ textAlign: "right" }}>
          <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => setDialogOpen(true)}>
            Open a bottle
          </Button>
        </Grid>
      </Grid>

      {!tableView ? (
        <LooseStock locationId={locationId} locationName={locationName} reloadKey={looseReload} search={search} />
      ) : status !== ApiStatus.LOADING && (containers?.length || 0) === 0 ? (
        <InventoryEmptyState
          height="75vh"
          icon={<LiquorOutlinedIcon />}
          title={expiringOnly ? "No Expiring Containers" : "No Open Containers"}
          description={
            expiringOnly
              ? "No open containers are nearing expiry for the selected filters. Adjust the filters or open a new container."
              : "Track partially-used bottles and packs by opening your first container. Monitor remaining volume and expiry at a glance."
          }
          actionLabel="Open a bottle"
          onAction={() => setDialogOpen(true)}
          caption="Need help? Check the Guide tab on inventory management"
        />
      ) : (
        <div style={{ height: "75vh", width: "100%" }}>
          <CustomDataGrid
            columns={columns}
            rows={containers}
            rowCount={containers.length}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            getRowId={(row: any) => row._id || Math.random().toString()}
            loading={status === ApiStatus.LOADING}
          />
        </div>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Open a container</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <SkuSelect value={dSkuId} onChange={onChangeSku} />
              {dSku && dSku.isStockTracked === false && (
                <p style={{ color: "#9da7b7", fontSize: 12, margin: "4px 0 0 14px" }}>Stock tracking is off for this item</p>
              )}
            </Grid>
            <Grid item xs={12}>
              <LocationSelect value={dLocationId} onChange={setDLocationId} />
            </Grid>
            {/* A batch-tracked item opens one container per batch */}
            {isBatchTracked && (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Batch"
                  value={dBatches.some((b) => b._id === dBatchId) ? dBatchId : ""}
                  onChange={(e) => setDBatchId(e.target.value)}
                  disabled={dBatchesLoading || dBatches.length === 0}
                  helperText={
                    dBatchesLoading
                      ? "loading batches..."
                      : !dLocationId
                        ? "select a location first"
                        : dBatches.length === 0
                          ? "No batch of this item has stock at this location"
                          : batchStockText()
                  }
                >
                  {dBatches.map((b: any) => (
                    <MenuItem key={b._id} value={b._id}>{batchLabel(b)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Container unit"
                value={dContainerUnit}
                onChange={(e) => setDContainerUnit(e.target.value)}
                disabled={!dSku}
                helperText={
                  !dSku
                    ? "select an item first"
                    : !dContainerUnit || unitFactor === 1
                      ? "the pack a container maps to (e.g. bottle)"
                      : `1 ${unitName(dContainerUnit)} = ${num(unitFactor)} ${baseUnit}`
                }
              >
                {unitOptionTokens(dSku).map((t: string) => (
                  <MenuItem key={t} value={t}>{unitName(t)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={dOpening}>Cancel</Button>
          <Button variant="contained" onClick={onOpen} disabled={!canOpen}>
            {dOpening ? "Opening..." : "Open"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpenContainersList;
